import React, { useRef, useEffect, useState, useCallback } from 'react';

interface DropdownPillOption {
  value: string | number | undefined;
  label: string;
}

interface DropdownPillProps {
  label: string;
  options: DropdownPillOption[];
  selectedValue?: string | number | undefined;
  onSelect: (value: string | number | undefined) => void;
  placeholder?: string;
  className?: string;
}

export const DropdownPill: React.FC<DropdownPillProps> = ({
  label,
  options,
  selectedValue,
  onSelect,
  placeholder,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === selectedValue);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleOptionClick = useCallback(
    (value: string | number | undefined) => {
      onSelect(value);
      setIsOpen(false);
    },
    [onSelect],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onSelect(undefined);
    },
    [onSelect],
  );

  // If a value is selected, show as pill with close button
  if (selectedOption) {
    return (
      <div ref={dropdownRef} className={`relative inline-flex ${className}`}>
        <div className="bg-blue-primary inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2 shadow-sm">
          <button
            onClick={handleClear}
            className="flex h-4 w-4 items-center justify-center text-white hover:text-gray-200"
            aria-label="Clear filter"
          >
            ×
          </button>
          <div className="text-sm font-semibold text-white">{selectedOption.label}</div>
        </div>
      </div>
    );
  }

  // If no value selected, show as regular dropdown button
  return (
    <div ref={dropdownRef} className={`relative inline-flex flex-col items-start justify-start ${className}`}>
      <button
        onClick={handleToggle}
        className="inline-flex items-center justify-center gap-1.5 rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
      >
        <div>{placeholder || label}</div>
        <div className="flex h-4 w-4 items-center justify-center">
          <svg
            className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      {isOpen && (
        <div className="absolute top-full z-10 mt-2 max-h-64 w-48 overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg">
          {options.map((option) => (
            <button
              key={option.value ?? 'all'}
              onClick={() => handleOptionClick(option.value)}
              className="w-full px-4 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
